import React, { useState, useMemo } from 'react';
import { Search, Trophy } from 'lucide-react';
import FilterModalWrapper from './FilterModalWrapper';

/**
 * LeagueModal - Desktop modal for selecting leagues
 * Provides searchable league list grouped by country
 * Adapted from mobile LeagueSheet
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {string|number} selectedLeague - Currently selected league ID
 * @param {Function} onLeagueSelect - Callback when league is selected
 * @param {Array} leagues - List of leagues
 */
const LeagueModal = ({
  isOpen,
  onClose,
  selectedLeague,
  onLeagueSelect,
  leagues = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Group leagues by country
  const groupedLeagues = useMemo(() => {
    if (!leagues || leagues.length === 0) return {};

    const grouped = {};
    leagues.forEach(league => {
      const country = league.country || 'International';
      if (!grouped[country]) {
        grouped[country] = [];
      }
      grouped[country].push(league);
    });

    // Sort countries alphabetically
    const sortedGroups = {};
    Object.keys(grouped).sort().forEach(key => {
      sortedGroups[key] = grouped[key].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    });

    return sortedGroups;
  }, [leagues]);

  // Filter leagues by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groupedLeagues;

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(groupedLeagues).forEach(([country, leagueList]) => {
      const filteredLeagues = leagueList.filter(league => {
        const name = (league.name || '').toLowerCase();
        const id = String(league.id || '').toLowerCase();
        return name.includes(query) || id.includes(query) || country.toLowerCase().includes(query);
      });

      if (filteredLeagues.length > 0) {
        filtered[country] = filteredLeagues;
      }
    });

    return filtered;
  }, [groupedLeagues, searchQuery]);

  const handleLeagueSelect = (league) => {
    onLeagueSelect(league.id);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    onLeagueSelect('');
    setSearchQuery('');
  };

  return (
    <FilterModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Select League"
      icon={Trophy}
      maxWidth="md"
      maxHeight="70vh"
    >
      {/* Search Bar */}
      <div className="p-4 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search leagues..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-emerald-500
                     border border-white/10"
          />
        </div>
      </div>

      {/* League List */}
      <div className="overflow-y-auto">
        {/* All Leagues Option */}
        <button
          onClick={handleClearSelection}
          className={`
            w-full flex items-center gap-3 px-4 py-3 transition-colors
            ${!selectedLeague || selectedLeague === ''
              ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
              : 'hover:bg-gray-800/50 text-white'
            }
          `}
        >
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
            <Trophy className="w-4 h-4 text-gray-400" />
          </div>
          <span className="font-medium">All Leagues</span>
          {(!selectedLeague || selectedLeague === '') && (
            <div className="ml-auto w-2 h-2 rounded-full bg-emerald-400" />
          )}
        </button>

        {/* Grouped Leagues */}
        {Object.entries(filteredGroups).map(([country, leagueList]) => (
          <div key={country}>
            <div className="px-4 py-2 bg-gray-800/50 border-y border-white/5">
              <span className="text-xs font-semibold text-gray-400 uppercase">
                {country}
              </span>
            </div>

            {leagueList.map((league) => {
              const isSelected = String(selectedLeague) === String(league.id);
              const leagueName = league.name || 'Unknown League';
              const leagueLogo = league.logo || league.image;

              return (
                <button
                  key={league.id}
                  onClick={() => handleLeagueSelect(league)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 transition-colors
                    ${isSelected
                      ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
                      : 'hover:bg-gray-800/50 text-white'
                    }
                  `}
                >
                  {leagueLogo ? (
                    <img
                      src={leagueLogo}
                      alt={leagueName}
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                      <Trophy className="w-4 h-4 text-gray-400" />
                    </div>
                  )}

                  <div className="flex-1 text-left">
                    <div className="font-medium">{leagueName}</div>
                    {league.type && (
                      <div className="text-xs text-gray-500">{league.type}</div>
                    )}
                  </div>

                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  )}
                </button>
              );
            })}
          </div>
        ))}

        {Object.keys(filteredGroups).length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500">No leagues found</p>
          </div>
        )}
      </div>
    </FilterModalWrapper>
  );
};

export default LeagueModal;
